import React, { useEffect, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import "./ReservationGeneratorAdmin.css";

const API = "/api/admin/reservation-generator";
const ACTIVE_STATUSES = new Set(["queued", "running"]);
const RESUMABLE_STATUSES = new Set(["interrupted", "failed"]);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DEFAULT_CONFIG = {
  date_mode: "fixed", start_date: "", end_date: "", start_offset_days: 1,
  window_days: 30, min_nights: 1, max_nights: 7, preferred_nights: 2,
  preferred_weight: 4, adults: 2, target_count: 20, target_mode: "count",
  target_occupancy_percent: 60, seed: "",
};
const DEFAULT_SCHEDULE = { name: "", frequency: "daily", time: "09:00", weekday: 1, is_enabled: false };

function jsonObject(value) {
  if (typeof value === "string") {
    try { return JSON.parse(value) || {}; } catch (_) { return {}; }
  }
  return value && typeof value === "object" ? value : {};
}

function errorText(error, fallback) {
  const message = error?.response?.data?.message || error?.response?.data?.error;
  return typeof message === "string" ? message : fallback;
}

function requestKey() {
  if (typeof window.crypto?.randomUUID === "function") return window.crypto.randomUUID();
  if (typeof window.crypto?.getRandomValues !== "function") throw new Error("Secure request-key generation is unavailable in this browser.");
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function buildGeneratorConfig(form, businessUnitId) {
  const config = {
    business_unit_id: Number(businessUnitId), date_mode: form.date_mode,
    min_nights: Number(form.min_nights), max_nights: Number(form.max_nights),
    preferred_nights: Number(form.preferred_nights), preferred_weight: Number(form.preferred_weight),
    adults: Number(form.adults), target_mode: form.target_mode, target_count: Number(form.target_count),
  };
  if (form.date_mode === "fixed") {
    config.start_date = form.start_date;
    config.end_date = form.end_date;
  } else {
    config.start_offset_days = Number(form.start_offset_days);
    config.window_days = Number(form.window_days);
  }
  if (form.target_mode === "occupancy") config.target_occupancy_percent = Number(form.target_occupancy_percent);
  if (form.seed !== "" && form.seed != null) config.seed = Number(form.seed);
  return config;
}

export function validateGeneratorConfig(config) {
  const integerBetween = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;
  if (!integerBetween(config.business_unit_id, 1, Number.MAX_SAFE_INTEGER)) return "Choose a Business Unit.";
  if (![config.min_nights, config.max_nights, config.preferred_nights].every((n) => integerBetween(n, 1, 60))) return "Stay lengths must be whole numbers between 1 and 60 nights.";
  if (config.min_nights > config.max_nights) return "Minimum nights cannot exceed maximum nights.";
  if (config.preferred_nights < config.min_nights || config.preferred_nights > config.max_nights) return "Preferred nights must fall between the minimum and maximum.";
  if (!integerBetween(config.adults, 1, 10)) return "Adults must be a whole number between 1 and 10.";
  if (!integerBetween(config.target_count, 1, 500)) return "Reservation count must be a whole number between 1 and 500.";
  if (config.target_mode === "occupancy" && !integerBetween(config.target_occupancy_percent, 1, 100)) return "Target occupancy must be a whole percentage between 1% and 100%.";
  if (config.seed !== undefined && !integerBetween(config.seed, 0, 4294967295)) return "Seed must be a whole number between 0 and 4,294,967,295.";
  let availableNights;
  if (config.date_mode === "fixed") {
    const validDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "") && !Number.isNaN(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
    if (!validDate(config.start_date) || !validDate(config.end_date)) return "Choose the first check-in date and the latest checkout date.";
    availableNights = (Date.parse(config.end_date) - Date.parse(config.start_date)) / 86400000;
    if (availableNights <= 0) return "Latest checkout must be after the first check-in date.";
    if (availableNights > 366) return "The date window cannot exceed 366 nights.";
  } else {
    if (!integerBetween(config.start_offset_days, 0, 365) || !integerBetween(config.window_days, 1, 366)) return "Use a start offset from 0 to 365 days and a window from 1 to 366 nights.";
    availableNights = config.window_days;
  }
  if (config.min_nights > availableNights) return "The date window must accommodate the minimum stay.";
  return "";
}

function ConfigSummary({ config, businessUnitName }) {
  return (
    <dl className="reservationGenerator__summary">
      <div><dt>Business Unit</dt><dd>{businessUnitName} · #{config.business_unit_id}</dd></div>
      <div><dt>Stay window</dt><dd>{config.date_mode === "fixed" ? `${config.start_date} → ${config.end_date} (latest checkout)` : `Starts in ${config.start_offset_days} days · ${config.window_days}-night window`}</dd></div>
      <div><dt>Length & guests</dt><dd>{config.min_nights}–{config.max_nights} nights · favor {config.preferred_nights} nights · {config.adults} adults</dd></div>
      <div><dt>Target</dt><dd>{config.target_mode === "occupancy" ? `${config.target_occupancy_percent}% average occupancy · at most ${config.target_count} new reservations` : `${config.target_count} new reservations`}</dd></div>
    </dl>
  );
}

function Status({ value }) {
  return <span className={`reservationGenerator__status reservationGenerator__status--${value}`}>{String(value || "unknown").replace(/_/g, " ")}</span>;
}

export default function ReservationGeneratorAdmin() {
  const [user] = useState(readStoredUser);
  const canAccess = Number(user?.id) === 1 || user?.permissions?.["reservations.generate_test"] === true;
  const [businessUnits, setBusinessUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [businessUnitId, setBusinessUnitId] = useState("");
  const [form, setForm] = useState(DEFAULT_CONFIG);
  const [mode, setMode] = useState("run");
  const [scheduleForm, setScheduleForm] = useState(DEFAULT_SCHEDULE);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [review, setReview] = useState(null);
  const [uncertain, setUncertain] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [historyError, setHistoryError] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [scheduleTimezone, setScheduleTimezone] = useState("");
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [reload, setReload] = useState(0);
  const mutationRef = useRef(false);
  const scopeRef = useRef(0);
  const selectedUnit = businessUnits.find((unit) => String(unit.id) === businessUnitId);
  const hasActiveRun = runs.some((run) => ACTIVE_STATUSES.has(run.status));
  const pendingStorageKey = (bu) => `reservation-generator:pending:${user?.id}:${bu}`;

  useEffect(() => {
    if (!canAccess) { setUnitsLoading(false); return undefined; }
    let active = true;
    axios.get("/api/business-units/all").then(({ data }) => {
      if (active) setBusinessUnits(Array.isArray(data) ? data : data?.business_units || []);
    }).catch((requestError) => {
      if (active) setError(errorText(requestError, "Could not load Business Units. Reload this page to try again."));
    }).finally(() => { if (active) setUnitsLoading(false); });
    return () => { active = false; };
  }, [canAccess]);

  useEffect(() => {
    if (!businessUnitId || !canAccess) return undefined;
    let active = true;
    let timer;
    async function refresh(initial = false) {
      if (initial) setHistoryLoading(true);
      const results = await Promise.allSettled([
        axios.get(`${API}/runs`, { params: { business_unit_id: Number(businessUnitId) } }),
        axios.get(`${API}/schedules`, { params: { business_unit_id: Number(businessUnitId) } }),
      ]);
      if (!active) return;
      const [runResult, scheduleResult] = results;
      let currentRuns = [];
      if (runResult.status === "fulfilled") {
        currentRuns = runResult.value.data?.runs || [];
        setRuns(currentRuns);
      }
      if (scheduleResult.status === "fulfilled") {
        setSchedules(scheduleResult.value.data?.schedules || []);
        setScheduleTimezone(scheduleResult.value.data?.timezone || "");
      }
      setHistoryError(results.some((result) => result.status === "rejected") ? "Some history could not be refreshed. Retrying automatically." : "");
      setHistoryLoading(false);
      timer = window.setTimeout(() => refresh(), currentRuns.some((run) => ACTIVE_STATUSES.has(run.status)) ? 5000 : 15000);
    }
    refresh(true);
    return () => { active = false; window.clearTimeout(timer); };
  }, [businessUnitId, canAccess, reload]);

  useEffect(() => {
    setDetail(null);
    setDetailError("");
    if (!selectedRunId || !businessUnitId) return undefined;
    let active = true;
    let timer;
    async function refresh() {
      try {
        const { data } = await axios.get(`${API}/runs/${selectedRunId}`);
        if (!active) return;
        setDetail(data);
        setDetailError("");
        if (ACTIVE_STATUSES.has(data?.run?.status)) timer = window.setTimeout(refresh, 5000);
      } catch (requestError) {
        if (!active) return;
        setDetailError(errorText(requestError, "Could not load run details. Retrying automatically."));
        timer = window.setTimeout(refresh, 10000);
      }
    }
    refresh();
    return () => { active = false; window.clearTimeout(timer); };
  }, [selectedRunId, businessUnitId, reload]);

  function changeBusinessUnit(value) {
    scopeRef.current += 1;
    setBusinessUnitId(value);
    setRuns([]); setSchedules([]); setScheduleTimezone(""); setSelectedRunId(null); setDetail(null);
    setError(""); setNotice(""); setHistoryError(""); setReview(null); setUncertain(false);
    setEditingScheduleId(null); setScheduleForm(DEFAULT_SCHEDULE); setMode("run");
    setForm(DEFAULT_CONFIG);
    try {
      const pending = JSON.parse(sessionStorage.getItem(pendingStorageKey(value)) || "null");
      if (pending?.key && Number(pending.config?.business_unit_id) === Number(value)) {
        setForm({ ...DEFAULT_CONFIG, ...pending.config });
        setMode(pending.mode === "schedule" ? "schedule" : "run");
        setScheduleForm({ ...DEFAULT_SCHEDULE, ...pending.schedule });
        setEditingScheduleId(pending.scheduleId || null);
        setReview(pending); setUncertain(true);
        setNotice(`A previous submission needs confirmation. Retry the same request to retrieve its ${pending.mode === "schedule" ? "schedule" : "run"} without creating duplicates.`);
      }
    } catch (_) { /* Storage can be unavailable; in-memory retries still retain the key. */ }
  }

  function changeMode(value) {
    setMode(value); setReview(null); setError(""); setNotice(""); setEditingScheduleId(null);
    setScheduleForm(DEFAULT_SCHEDULE);
    if (value === "schedule") setForm((current) => ({ ...current, date_mode: "rolling" }));
  }

  function reviewConfiguration(event) {
    event.preventDefault();
    const config = buildGeneratorConfig(form, businessUnitId);
    const validationError = validateGeneratorConfig(config);
    if (validationError) { setError(validationError); return; }
    if (mode === "schedule" && !scheduleForm.name.trim()) { setError("Give this schedule a name."); return; }
    setError(""); setNotice("");
    try {
      setReview({ config, key: requestKey(), mode, schedule: { ...scheduleForm, name: scheduleForm.name.trim(), weekday: Number(scheduleForm.weekday) }, scheduleId: editingScheduleId });
    } catch (keyError) { setError(keyError.message); }
  }

  async function submitReviewed() {
    if (!review || mutationRef.current || review.config.business_unit_id !== Number(businessUnitId)) return;
    mutationRef.current = true; setBusy(true); setError(""); setNotice("");
    const scope = scopeRef.current;
    try {
      try { sessionStorage.setItem(pendingStorageKey(businessUnitId), JSON.stringify(review)); } catch (_) { /* Optional recovery after reload. */ }
      if (review.mode === "run") {
        const { data } = await axios.post(`${API}/runs`, { config: review.config, idempotency_key: review.key });
        if (!data?.run?.id) throw new Error("The server did not return a run identifier.");
        if (scope !== scopeRef.current) return;
        setSelectedRunId(data.run.id);
        setRuns((current) => [data.run, ...current.filter((run) => run.id !== data.run.id)]);
        setNotice(`Run #${data.run.id} accepted. It continues in the background if you leave this page.`);
      } else {
        const payload = { ...review.schedule, config: review.config, idempotency_key: review.key };
        const { data } = review.scheduleId
          ? await axios.patch(`${API}/schedules/${review.scheduleId}`, payload)
          : await axios.post(`${API}/schedules`, payload);
        if (!data?.schedule?.id) throw new Error("The server did not return a schedule identifier.");
        if (scope !== scopeRef.current) return;
        setNotice(review.schedule.is_enabled ? "Schedule saved and enabled." : "Schedule saved in paused state.");
        setEditingScheduleId(null); setScheduleForm(DEFAULT_SCHEDULE);
      }
      try { sessionStorage.removeItem(pendingStorageKey(businessUnitId)); } catch (_) { /* In-memory state remains authoritative. */ }
      setReview(null); setUncertain(false); setReload((value) => value + 1);
    } catch (requestError) {
      if (scope !== scopeRef.current) return;
      const status = requestError?.response?.status;
      const mayHaveStarted = !status || status >= 500 || status === 408;
      setUncertain(mayHaveStarted);
      if (!mayHaveStarted) {
        try { sessionStorage.removeItem(pendingStorageKey(businessUnitId)); } catch (_) { /* No pending request. */ }
      }
      setError(mayHaveStarted
        ? `The submission could not be confirmed. Retry the same ${review.mode === "schedule" ? "schedule" : "generation"} request; its key prevents ${review.mode === "schedule" ? "duplicate schedules" : "duplicate reservations"}.`
        : errorText(requestError, review.mode === "run" ? "Could not start this run." : "Could not save this schedule."));
      setReload((value) => value + 1);
    } finally { mutationRef.current = false; setBusy(false); }
  }

  async function resumeRun(run) {
    if (mutationRef.current) return;
    mutationRef.current = true; setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/runs/${run.id}/resume`);
      setSelectedRunId(data?.run?.id || run.id);
      setNotice(`Run #${run.id} resumed.`); setReload((value) => value + 1);
    } catch (requestError) { setError(errorText(requestError, "Could not resume this run. Refresh its status before retrying.")); }
    finally { mutationRef.current = false; setBusy(false); }
  }

  function editSchedule(schedule) {
    setMode("schedule"); setEditingScheduleId(schedule.id); setReview(null); setError(""); setNotice("");
    const config = jsonObject(schedule.config || schedule.config_json);
    setForm({ ...DEFAULT_CONFIG, ...config, seed: config.seed ?? "", date_mode: "rolling" });
    setScheduleForm({ name: schedule.name || "", frequency: schedule.frequency, time: String(schedule.time || "09:00").slice(0, 5), weekday: schedule.weekday || 1, is_enabled: Boolean(schedule.is_enabled) });
    document.getElementById("reservation-generator-config")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }

  async function toggleSchedule(schedule) {
    if (mutationRef.current) return;
    mutationRef.current = true; setBusy(true); setError("");
    try {
      await axios.patch(`${API}/schedules/${schedule.id}`, { is_enabled: !schedule.is_enabled });
      setNotice(`Schedule ${schedule.is_enabled ? "paused" : "enabled"}.`); setReload((value) => value + 1);
    } catch (requestError) { setError(errorText(requestError, "Could not update this schedule.")); }
    finally { mutationRef.current = false; setBusy(false); }
  }

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };
  const updateScheduleField = (event) => {
    const { name, type, checked, value } = event.target;
    setScheduleForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };
  const selectedRun = detail?.run;
  const reasons = Object.entries(jsonObject(selectedRun?.reasons_json)).sort((a, b) => Number(b[1]) - Number(a[1]));
  const timezone = scheduleTimezone || schedules.find((schedule) => schedule.timezone)?.timezone || selectedUnit?.timezone;
  const createdItems = (detail?.items || []).filter((item) => item.reservation_group_id && (!item.status || item.status === "created"));

  if (!canAccess) return <div className="alert alert-danger" role="alert">Permission to generate test reservations is required.</div>;

  return (
    <div className="reservationGenerator">
      <header className="reservationGenerator__header">
        <div><p className="reservationGenerator__eyebrow">Administration</p><h1>Test reservation generator</h1><p>Create stays using existing BU customers, PMS rates and the manual reservation checks.</p></div>
        <span className="reservationGenerator__badge">PMS reservations</span>
      </header>
      <div className="reservationGenerator__inventoryNote">Generated reservations consume real occupancy in the selected Business Unit.</div>
      {error && <div role="alert" className="reservationGenerator__error">{error}</div>}
      {notice && <div role="status" className="reservationGenerator__notice">{notice}</div>}
      <section className="reservationGenerator__card" id="reservation-generator-config" aria-labelledby="generator-config-title">
        <div className="reservationGenerator__sectionHeading"><h2 id="generator-config-title">1. Configure generation</h2><span>One room per reservation</span></div>
        <label className="reservationGenerator__bu">Business Unit
          <select name="business_unit_id" aria-label="Business Unit" value={businessUnitId} onChange={(event) => changeBusinessUnit(event.target.value)} disabled={unitsLoading || busy}>
            <option value="">{unitsLoading ? "Loading Business Units…" : "Choose a Business Unit"}</option>
            {businessUnits.map((unit) => <option key={unit.id} value={unit.id}>{unit.name} · #{unit.id}</option>)}
          </select>
        </label>
        {!unitsLoading && businessUnits.length === 0 && !error && <p>No Business Units are available.</p>}
        <form onSubmit={reviewConfiguration}>
          <fieldset disabled={!businessUnitId || busy || Boolean(review)}>
            <legend className="reservationGenerator__srOnly">Reservation generation settings</legend>
            <div className="reservationGenerator__mode" aria-label="Generation mode">
              <button type="button" aria-pressed={mode === "run"} onClick={() => changeMode("run")}>Run once</button>
              <button type="button" aria-pressed={mode === "schedule"} onClick={() => changeMode("schedule")}>Schedule</button>
            </div>
            {mode === "schedule" && <div className="reservationGenerator__fields reservationGenerator__scheduleFields">
              <label>Schedule name<input name="name" value={scheduleForm.name} onChange={updateScheduleField} maxLength={120} required /></label>
              <label>Frequency<select name="frequency" value={scheduleForm.frequency} onChange={updateScheduleField}><option value="daily">Daily</option><option value="weekly">Weekly</option></select></label>
              {scheduleForm.frequency === "weekly" && <label>Weekday<select name="weekday" value={scheduleForm.weekday} onChange={updateScheduleField}>{WEEKDAYS.map((day, index) => <option key={day} value={index + 1}>{day}</option>)}</select></label>}
              <label>Time<input name="time" type="time" value={scheduleForm.time} onChange={updateScheduleField} required /><small>Business Unit local time{timezone ? ` · ${timezone}` : ""}</small></label>
              <label className="reservationGenerator__checkbox"><input name="is_enabled" type="checkbox" checked={scheduleForm.is_enabled} onChange={updateScheduleField} />Enable after saving</label>
            </div>}
            <div className="reservationGenerator__fields">
              {mode === "run" && <label>Date window<select name="date_mode" value={form.date_mode} onChange={updateField}><option value="fixed">Fixed dates</option><option value="rolling">Rolling window</option></select></label>}
              {form.date_mode === "fixed" ? <>
                <label>First check-in<input name="start_date" type="date" value={form.start_date} onChange={updateField} required /></label>
                <label>Latest checkout<input name="end_date" type="date" value={form.end_date} min={form.start_date || undefined} onChange={updateField} required /></label>
              </> : <>
                <label>Start offset (days)<input name="start_offset_days" type="number" min="0" max="365" step="1" value={form.start_offset_days} onChange={updateField} required /><small>0 = today, 1 = tomorrow</small></label>
                <label>Window (nights)<input name="window_days" type="number" min="1" max="366" step="1" value={form.window_days} onChange={updateField} required /><small>Entire stay must fit in this window</small></label>
              </>}
              <label>Minimum nights<input name="min_nights" type="number" min="1" max="60" step="1" value={form.min_nights} onChange={updateField} required /></label>
              <label>Maximum nights<input name="max_nights" type="number" min="1" max="60" step="1" value={form.max_nights} onChange={updateField} required /></label>
              <label>Preferred nights<input name="preferred_nights" type="number" min="1" max="60" step="1" value={form.preferred_nights} onChange={updateField} required /><small>Selected more often when valid</small></label>
              <label>Adults per room<input name="adults" type="number" min="1" max="10" step="1" value={form.adults} onChange={updateField} required /></label>
              <label>Target<select name="target_mode" value={form.target_mode} onChange={updateField}><option value="count">Number of reservations</option><option value="occupancy">Occupancy target</option></select></label>
              {form.target_mode === "occupancy" && <label>Average occupancy target (%)<input name="target_occupancy_percent" type="number" min="1" max="100" step="1" value={form.target_occupancy_percent} onChange={updateField} required /></label>}
              <label>{form.target_mode === "occupancy" ? "Maximum new reservations per run" : "Reservations per run"}<input name="target_count" type="number" min="1" max="500" step="1" value={form.target_count} onChange={updateField} required /></label>
            </div>
            <p className="reservationGenerator__help">Bookers are alternated. Guests come from the selected BU and must be eligible for the stay. Only sellable PMS rates with a price for every night are used. The final count may be lower when valid combinations run out.</p>
            {form.target_mode === "occupancy" && <p className="reservationGenerator__help">Fill toward the average occupancy across the selected nights, up to the new-reservation limit for each run.</p>}
            <details className="reservationGenerator__advanced"><summary>Advanced options</summary><label>Random seed (optional)<input name="seed" type="number" min="0" max="4294967295" step="1" placeholder="Automatic" value={form.seed} onChange={updateField} /><small>Reuse a seed to repeat selection order. Availability is always checked again.</small></label></details>
            <button type="submit" className="reservationGenerator__primary">Review configuration</button>
          </fieldset>
        </form>
        {review && <section className="reservationGenerator__review" aria-label="Review configuration">
          <h2>2. Review {review.mode === "run" ? "generation" : "schedule"}</h2>
          <ConfigSummary config={review.config} businessUnitName={selectedUnit?.name || "Business Unit"} />
          {review.mode === "schedule" && <p>{review.schedule.name} · {review.schedule.frequency === "weekly" ? `Every ${WEEKDAYS[review.schedule.weekday - 1]}` : "Daily"} at {review.schedule.time}, Business Unit local time · {review.schedule.is_enabled ? "Enabled" : "Paused"}</p>}
          <p>These reservations will use existing customers and consume real occupancy.</p>
          <div className="reservationGenerator__actions">
            <button type="button" className="reservationGenerator__primary" disabled={busy || (hasActiveRun && review.mode === "run" && !uncertain)} onClick={submitReviewed}>{busy ? "Submitting…" : uncertain ? `Retry same ${review.mode === "schedule" ? "schedule" : "generation"} request` : review.mode === "schedule" ? "Save schedule" : "Generate reservations"}</button>
            <button type="button" disabled={busy || uncertain} onClick={() => { setReview(null); setError(""); }}>Edit configuration</button>
          </div>
          {hasActiveRun && review.mode === "run" && !uncertain && <p>A run is already active for this BU. Wait for it to finish before starting another.</p>}
        </section>}
      </section>

      {businessUnitId && <>
        <section className="reservationGenerator__card" aria-labelledby="generator-history-title">
          <div className="reservationGenerator__sectionHeading"><div><h2 id="generator-history-title">Run history</h2><p>Background progress refreshes automatically.</p></div><button type="button" disabled={historyLoading} onClick={() => setReload((value) => value + 1)}>Refresh</button></div>
          {historyError && <p role="alert" className="reservationGenerator__error">{historyError}</p>}
          {historyLoading && runs.length === 0 ? <p role="status">Loading runs…</p> : runs.length === 0 ? <p className="reservationGenerator__empty">No generation runs for this BU yet.</p> : <div className="reservationGenerator__tableWrap"><table><thead><tr><th>Run</th><th>Started</th><th>Status</th><th>Created / target</th><th>Attempts</th><th>Actions</th></tr></thead><tbody>{runs.map((run) => <tr key={run.id} className={String(selectedRunId) === String(run.id) ? "reservationGenerator__selectedRow" : undefined}>
            <td>#{run.id}{run.schedule_id ? <small>Scheduled</small> : null}</td><td>{run.created_at ? new Date(run.created_at).toLocaleString() : "—"}</td><td><Status value={run.status} /></td><td>{run.created_count || 0} / {run.target_count || jsonObject(run.config || run.config_json).target_count || "—"}</td><td>{run.attempts || 0}</td><td><div className="reservationGenerator__actions"><button type="button" onClick={() => setSelectedRunId(run.id)}>View run #{run.id}</button>{RESUMABLE_STATUSES.has(run.status) && <button type="button" disabled={busy || hasActiveRun} onClick={() => resumeRun(run)}>Resume</button>}</div></td>
          </tr>)}</tbody></table></div>}
        </section>
        {selectedRunId && <section className="reservationGenerator__card" aria-label={`Run ${selectedRunId} details`}>
          <div className="reservationGenerator__sectionHeading"><h2>Run #{selectedRunId}</h2><button type="button" onClick={() => setSelectedRunId(null)}>Close details</button></div>
          {detailError && <p role="alert" className="reservationGenerator__error">{detailError}</p>}
          {!detail && !detailError && <p role="status">Loading run details…</p>}
          {selectedRun && <><div className="reservationGenerator__metrics"><Status value={selectedRun.status} /><strong>{selectedRun.created_count || 0} reservations created</strong><span>{selectedRun.attempts || 0} attempts</span></div>
            {selectedRun.error_message && <p className="reservationGenerator__error">{selectedRun.error_message}</p>}
            {reasons.length > 0 && <div className="reservationGenerator__reasons"><h3>Reasons candidates were skipped</h3><ul>{reasons.map(([reason, count]) => <li key={reason}><span>{reason.replace(/_/g, " ")}</span><strong>{typeof count === "object" ? JSON.stringify(count) : String(count)}</strong></li>)}</ul></div>}
            {createdItems.length > 0 ? <div className="reservationGenerator__tableWrap"><table><thead><tr><th>Reservation</th><th>Check-in</th><th>Checkout</th><th>Main guest</th><th>Booker</th></tr></thead><tbody>{createdItems.map((item) => <tr key={item.id || item.reservation_group_id}><td><a href={`/reservation-group/${encodeURIComponent(item.reservation_group_id)}?mode=view&business_unit_id=${encodeURIComponent(businessUnitId)}`}>Group #{item.reservation_group_id}</a></td><td>{String(item.check_in_date || "").slice(0, 10)}</td><td>{String(item.check_out_date || "").slice(0, 10)}</td><td>{item.main_guest_customer_id || item.main_guest_id || "—"}</td><td>{item.booker_customer_id || item.booker_id || "—"}</td></tr>)}</tbody></table></div> : <p className="reservationGenerator__empty">No reservations recorded in this run yet.</p>}
          </>}
        </section>}
        <section className="reservationGenerator__card" aria-labelledby="generator-schedules-title">
          <div className="reservationGenerator__sectionHeading"><div><h2 id="generator-schedules-title">Schedules</h2><p>Recurring runs use a rolling date window and the same reservation checks.</p></div></div>
          {historyLoading && schedules.length === 0 ? <p role="status">Loading schedules…</p> : schedules.length === 0 ? <p className="reservationGenerator__empty">No schedules saved for this BU. Choose Schedule above to create one.</p> : <div className="reservationGenerator__scheduleList">{schedules.map((schedule) => {
            const config = jsonObject(schedule.config || schedule.config_json);
            return <article key={schedule.id} className="reservationGenerator__scheduleItem"><div><h3>{schedule.name}</h3><p>{schedule.frequency === "weekly" ? `Every ${WEEKDAYS[Number(schedule.weekday) - 1]}` : "Daily"} · {String(schedule.time || "").slice(0, 5)} · {schedule.timezone || "BU local time"}</p><p>{config.target_mode === "occupancy" ? `Fill toward ${config.target_occupancy_percent}% average occupancy, up to ${config.target_count} new reservations` : `${config.target_count} reservations per run`} · {config.window_days}-night window</p>{schedule.next_run_at && <small>Next run: {new Date(schedule.next_run_at).toLocaleString()}</small>}{schedule.last_error && <p className="reservationGenerator__error">{schedule.last_error}</p>}</div><div className="reservationGenerator__actions"><Status value={schedule.is_enabled ? "enabled" : "paused"} /><button type="button" disabled={busy || uncertain || Boolean(review)} onClick={() => editSchedule(schedule)}>Edit</button><button type="button" disabled={busy || Boolean(review)} onClick={() => toggleSchedule(schedule)}>{schedule.is_enabled ? "Pause" : "Enable"}</button></div></article>;
          })}</div>}
        </section>
      </>}
    </div>
  );
}
